import React, { useEffect, useState } from 'react';
import './Cart.scss';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import { find, isNil, map } from 'lodash-es';
import { useAuth0 } from '@auth0/auth0-react';
import loader from '../../../assets/lotties/loader.json';
import { useCreateOrderMutation } from '../../../store/order/orderApi';
import { selectSelectedRestaurantId, selectTableSessionId } from '../../../store/auth/selectors';
import { ensureDataUrl } from '../../../utils/imageHandlers';
import { ItemPrice } from '../../../Item/ItemPrice/ItemPrice';
import { useModal } from '../../../providers/ModalProvider/ModalProvider';
import { AuthLogin } from '../../../login/login';
import { useGetUserCartQuery, useRemoveItemFromCartMutation } from '../../../store/cart/cartApi';
import { ICreateOrderItemFromMenuItemRequest } from '../../../proto/order/orderItem';
import { IGetCartItemResponse } from '../../../proto/cart/cartItem';
import { IGetMenuItemCustomizationGroupResponse } from '../../../proto/menu/item/menuItemCustomizationGroup';
import { IGetCartItemCustomizationGroupSelectionResponse } from '../../../proto/cart/cartItemCustomizationGroupSelection';
import { useGetRestaurantByIdQuery } from '../../../store/restaurant/restaurantApi';
import { useCreateUserOrderSessionMutation } from '../../../store/users/usersApi';

interface ICartProps {
	onDismiss: () => void;
	isOpen: boolean;
}

export const Cart = ({ onDismiss, isOpen }: ICartProps): React.JSX.Element => {
	const { data: cart, isLoading: isLoadingCart } = useGetUserCartQuery();
	const { openModal } = useModal();
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
	const { isAuthenticated } = useAuth0();
	const [open, setOpen] = useState(false);
	const [createOrder] = useCreateOrderMutation();
	const tableSessionId = useSelector(selectTableSessionId);
	const restaurantId = useSelector(selectSelectedRestaurantId);
	const { data: restaurant, isLoading: isLoadingRestaurant } = useGetRestaurantByIdQuery(restaurantId, { skip: isNil(restaurantId) });
	const [removeItemFromCart] = useRemoveItemFromCartMutation();
	const [createUserOrderSession] = useCreateUserOrderSessionMutation();

	const onDismissSheet = (): void => {
		setOpen(false);
		onDismiss();
	};

	useEffect(() => {
		setOpen(isOpen);
	}, [isOpen]);

	const placeOrder = async (): Promise<void> => {
		const cartItems: ICreateOrderItemFromMenuItemRequest[] = map(cart.items, (cartItem: IGetCartItemResponse) => ({
			menuItemId: cartItem.menuItem.id,
			quantity: cartItem.quantity,
			customizationGroups: map(cartItem.customizationGroups, (customizationGroup) => ({
				menuItemCustomizationGroupId: customizationGroup.menuItemCustomizationGroup.id,
				selections: map(customizationGroup.selections, (selection) => ({
					quantity: selection.quantity,
					customizationGroupOptionId: selection.option.id,
				})),
			})),
		}));

		// This should be after payment integration
		// TODO: Check here: if restaurant supports sessions - show the user the option to create a session or pay right away
		// TODO: If not, just pay right away
		let userOrderSessionId: string = null;

		if (restaurant.allowSessions) {
			const userOrderSession = await createUserOrderSession({ tableSessionId }).unwrap();

			userOrderSessionId = userOrderSession.id;
		}

		createOrder({ items: cartItems, userOrderSessionId, tableSessionId })
			.unwrap()
			.then(() => {
				onDismissSheet();
				toast.success('ההזמנה בוצעה בהצלחה!');
			})
			.catch((error) => {
				console.error('Error creating order:', error);
				toast.error('אירעה שגיאה בעת ביצוע ההזמנה. אנא נסה שוב מאוחר יותר.');
			});
	};

	const submitForm = (): void => {
		if (!isAuthenticated) {
			const closeModal = openModal(
				<AuthLogin
					onClose={(): void => {
						closeModal();
						setIsLoginModalOpen(false);
					}}
					onLogin={(): void => {
						closeModal();
						setIsLoginModalOpen(false);
						placeOrder();
					}}
				/>
			);

			setIsLoginModalOpen(true);
		} else {
			placeOrder();
		}
	};

	const handleRemoveItem = (cartItemId: string): void => {
		removeItemFromCart({ cartItemId });
	};

	const getSelectionSummaryString = (
		customizationGroup: IGetMenuItemCustomizationGroupResponse,
		selections: IGetCartItemCustomizationGroupSelectionResponse[]
	): string => {
		return map(selections, (selection) => {
			const option = find(customizationGroup.options, (customizationGroupOption) => customizationGroupOption.id === selection.option.id);

			return option?.name ?? '';
		}).join(', ');
	};

	const mapCartItemToOrderItem = (cartItem: IGetCartItemResponse): ICreateOrderItemFromMenuItemRequest => ({
		menuItemId: cartItem.menuItem.id,
		quantity: cartItem.quantity,
		customizationGroups: map(cartItem.customizationGroups, (customizationGroup) => ({
			menuItemCustomizationGroupId: customizationGroup.menuItemCustomizationGroup.id,
			selections: map(customizationGroup.selections, (selection) => ({
				quantity: selection.quantity,
				customizationGroupOptionId: selection.option.id,
			})),
		})),
	});

	return (
		<div>
			<BottomSheet
				open={open}
				onDismiss={onDismissSheet}
				defaultSnap={({ snapPoints, lastSnap }): any => lastSnap ?? Math.min(...snapPoints)}
				snapPoints={({ maxHeight }): any => [maxHeight - maxHeight / 5, maxHeight]}
				header={<div>עגלת קניות</div>}
				className="bottom-sheet-container"
				expandOnContentDrag={false}
				blocking={!isLoginModalOpen}
			>
				{isLoadingCart || isLoadingRestaurant ? (
					<Lottie animationData={loader} loop style={{ width: '10rem' }} />
				) : (
					<div className="bottom-sheet-content">
						<div className="cart-container">
							{map(cart?.items, (cartItem) => (
								<div className="cart-item" key={cartItem.id}>
									<div className="cart-item-image-and-content">
										<div className="cart-item-image">
											<img src={ensureDataUrl(cartItem.menuItem.image)} alt="title" />
										</div>
										<div className="cart-item-content">
											<div className="cart-item-title-and-quantity">
												<div className="cart-item-title">{cartItem.menuItem.title}</div>
												<div className="cart-item-quantity">x{cartItem.quantity}</div>
											</div>
											<div className="cart-item-description">{cartItem.menuItem.description}</div>
											{map(cartItem.customizationGroups, (customizationGroup) => (
												<div className="cart-item-option" key={customizationGroup.id}>
													<div className="cart-item-option-title">{customizationGroup.menuItemCustomizationGroup.title}:</div>
													<div className="cart-item-option-selections">
														{getSelectionSummaryString(
															customizationGroup.menuItemCustomizationGroup,
															customizationGroup.selections
														)}
													</div>
												</div>
											))}

											<div className="cart-item-price">
												<ItemPrice item={mapCartItemToOrderItem(cartItem)} menuItem={cartItem.menuItem} />
											</div>
										</div>
									</div>
									<div className="remove-item" onClick={(): void => handleRemoveItem(cartItem.id)}>
										<FontAwesomeIcon icon={faXmark} />
									</div>
								</div>
							))}
						</div>
						<div className="bottom-sheet-footer-transparent" />
						<div className="bottom-sheet-footer">
							<div className="order-button" onClick={submitForm}>
								<span className="add-to-order">הזמן</span>
							</div>
						</div>
					</div>
				)}
			</BottomSheet>
		</div>
	);
};
